/**
 * Website section sub-navigation.
 *
 * Nine content pages used to sit as nine sidebar entries, nearly half the
 * sidebar, and the half a club touches least after setup. They now live behind
 * one "Website" entry with this strip, mirroring Club Management.
 *
 * Rendered once from App's <main>, not per page: TeamNav is included by each of
 * its pages individually, which would have meant editing nine files and
 * remembering the tenth.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './WebsiteNav.css';

/** Route → label. The order a club actually sets a site up in. */
export const WEBSITE_TABS = [
  { to: '/content', label: 'Pages' },
  { to: '/news', label: 'News' },
  { to: '/events', label: 'Events' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/documents', label: 'Documents' },
  { to: '/venues', label: 'Where We Play' },
  { to: '/club-staff', label: 'Club Staff' },
  { to: '/sponsors', label: 'Sponsors' },
  { to: '/affiliations', label: 'Affiliations' },
  { to: '/products', label: 'Shop' },
];

/** Every route the Website section owns, including ones with no tab of their own. */
export const WEBSITE_ROUTES = [...WEBSITE_TABS.map(t => t.to), '/orders'];

export function isWebsiteRoute(pathname: string): boolean {
  return WEBSITE_ROUTES.some(r => pathname === r || pathname.startsWith(`${r}/`));
}

export interface WebsiteTab { to: string; label: string; gated?: boolean }

/**
 * The tabs a given plan sees. Gates are copied from the sidebar entries these
 * replaced, NOT re-derived: eight of the ten were ungated, and an earlier draft
 * that invented feature-flag checks would have hidden Shop from every club
 * without that flag.
 *
 * Shared by the sidebar sub-list and the mobile strip so the two can never
 * disagree about which pages exist.
 */
export function websiteTabsFor(plan: string | null | undefined): WebsiteTab[] {
  const clubStaffEnabled = plan === 'club' || plan === 'pro';
  const isFree = plan === 'free';
  // News and Club Staff are Club+. Both stay visible to a free club with an
  // Upgrade badge rather than disappearing: a club cannot buy what it cannot see.
  return WEBSITE_TABS.map(t =>
    (t.to === '/news' ? isFree : t.to === '/club-staff' ? !clubStaffEnabled : false)
      ? { ...t, gated: true }
      : t,
  );
}

export default function WebsiteNav() {
  const { plan } = useAuth();
  const location = useLocation();
  if (!isWebsiteRoute(location.pathname)) return null;

  return (
    <nav className="wn-nav" aria-label="Website sections">
      <div className="wn-tabs">
        {websiteTabsFor(plan).map(tab => (
          <NavLink
            key={tab.to}
            to={tab.gated ? '/upgrade' : tab.to}
            className={({ isActive }) =>
              `wn-tab ${tab.gated ? 'gated' : ''} ${isActive && !tab.gated ? 'active' : ''}`
            }
          >
            {tab.label}
            {tab.gated && <span className="wn-badge">Upgrade</span>}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
